import React from 'react';

import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
  PaginationLink,
} from '@/components/ui/pagination';

export const generatePaginationLinks = (
  currentPage: number,
  totalPages: number,
  onPageChange: (page: number) => void,
) => {
  const pages: React.JSX.Element[] = [];

  if (totalPages <= 1) return pages;

  const makeLink = (i: number) => (
    <PaginationItem key={i}>
      <PaginationLink
        to="."
        onClick={(e) => {
          e.preventDefault();
          onPageChange(i);
        }}
        isActive={i === currentPage}
      >
        {i}
      </PaginationLink>
    </PaginationItem>
  );

  const midStart = Math.max(2, currentPage - 1);
  const midEnd = Math.min(totalPages - 1, currentPage + 1);

  pages.push(makeLink(1));

  if (midStart > 2) {
    pages.push(<PaginationEllipsis key="ellipsis-start" />);
  }

  for (let i = midStart; i <= midEnd; i++) {
    pages.push(makeLink(i));
  }

  if (midEnd < totalPages - 1) {
    pages.push(<PaginationEllipsis key="ellipsis-end" />);
  }

  pages.push(makeLink(totalPages));

  return pages;
};

export type PaginatorProps = {
  currentPage: number;
  totalPages: number;
  onPageChange: (pageNumber: number) => void;
  showPreviousNext: boolean;
  className?: string;
  hideOnSinglePage?: boolean;
};

export default function Paginator({
  currentPage,
  totalPages,
  onPageChange,
  showPreviousNext,
  className,
  hideOnSinglePage = false,
}: PaginatorProps) {
  if (hideOnSinglePage && totalPages <= 1) return null;

  return (
    <Pagination className={className}>
      <PaginationContent>
        {showPreviousNext && totalPages ? (
          <PaginationItem>
            <PaginationPrevious
              to="."
              onClick={(e) => {
                e.preventDefault();
                onPageChange(currentPage - 1);
              }}
              disabled={currentPage - 1 < 1}
            />
          </PaginationItem>
        ) : null}
        {generatePaginationLinks(currentPage, totalPages, onPageChange)}
        {showPreviousNext && totalPages ? (
          <PaginationItem>
            <PaginationNext
              to="."
              onClick={(e) => {
                e.preventDefault();
                onPageChange(currentPage + 1);
              }}
              disabled={currentPage > totalPages - 1}
            />
          </PaginationItem>
        ) : null}
      </PaginationContent>
    </Pagination>
  );
}
